import { useAuthStore, useAuthModal } from '@/features/auth/store'
import router from '@/router'
import type { AxiosError } from 'axios'

interface AccountMergeErrorResponse {
  message?: string
}

type AccountMergeAxiosError = AxiosError<string | AccountMergeErrorResponse>

enum AuthResultType {
  ThirdPartyAuthSuccess = 'THIRD_PARTY_AUTH_SUCCESS',
  ThirdPartyAuthError = 'THIRD_PARTY_AUTH_ERROR',
  AccountMerged = 'ACCOUNT_MERGED'
}

const AUTH_RESULT_STORAGE_KEY = 'auth_result'
const RESET_PASSWORD_TOKEN_KEY = 'reset_password_token'
const isClient = typeof window !== 'undefined'

const isAccountMergeError = (error: unknown): error is AccountMergeAxiosError => {
  if (!error || typeof error !== 'object') {
    return false
  }

  const axiosError = error as Partial<AccountMergeAxiosError>
  const responseData = axiosError.response?.data

  if (!axiosError.response || axiosError.response.status !== 401) {
    return false
  }

  if (typeof responseData === 'string') {
    return responseData.includes('accounts have been successfully merged')
  }

  return typeof responseData?.message === 'string'
}

const extractErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error
  }

  if (error instanceof Error) {
    return error.message
  }

  if (error && typeof error === 'object' && 'toString' in error) {
    return String(error)
  }

  return 'Unknown error occurred'
}

export const handleCodeExchange = async (code: string): Promise<void> => {
  const authStore = useAuthStore()

  try {
    await authStore.exchangeCodeForTokens(code)

    if (isClient) {
      window.localStorage.setItem(
        AUTH_RESULT_STORAGE_KEY,
        JSON.stringify({ type: AuthResultType.ThirdPartyAuthSuccess })
      )
      // Close the popup — main window handles the rest via storage event
      // window.close()
      return
    }
  } catch (error: unknown) {
    console.error('Code exchange failed:', error)

    if (!isClient) {
      return
    }

    if (isAccountMergeError(error)) {
      const mergeMessage =
        typeof error.response?.data === 'string'
          ? error.response.data
          : error.response?.data?.message

      window.localStorage.setItem(
        AUTH_RESULT_STORAGE_KEY,
        JSON.stringify({ type: AuthResultType.AccountMerged, message: mergeMessage })
      )
      // window.close()
      return
    }

    window.localStorage.setItem(
      AUTH_RESULT_STORAGE_KEY,
      JSON.stringify({
        type: AuthResultType.ThirdPartyAuthError,
        error: extractErrorMessage(error)
      })
    )
    // window.close()
  } finally {
    // Fallback if window.close() didn't work (e.g., not opened via JS)
    if (isClient && !window.closed) {
      await router.push({ path: '/' })
    }
  }
}

export const handleResetToken = (token: string): void => {
  const authModal = useAuthModal()
  if (isClient) {
    window.localStorage.setItem(RESET_PASSWORD_TOKEN_KEY, token)
  }
  authModal.showAuthModal('setNewPassword')
}
